import React, { useState, useMemo } from 'react';
import { FaEdit, FaTrashAlt, FaSearch, FaFilter, FaHome, FaUsers, FaCalendarAlt, FaClipboardList, FaChartLine, FaEllipsisV, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import type { Visitation } from '../../types/visitation';

interface VisitationListProps {
  visitations: Visitation[];
  onEdit: (visitation: Visitation) => void;
  onDelete: (id: string) => void;
  isLoading?: boolean;
  error?: string | null;
}

interface FilterOptions {
  search: string;
  status: string;
  type: string;
  startDate: string;
  endDate: string;
}

const VisitationList: React.FC<VisitationListProps> = ({
  visitations,
  onEdit,
  onDelete,
  isLoading = false,
  error = null
}) => {
  const [filters, setFilters] = useState<FilterOptions>({
    search: '',
    status: 'all',
    type: 'all',
    startDate: '',
    endDate: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // 필터링 로직
  const filteredVisitations = useMemo(() => {
    return visitations.filter(visitation => {
      // 검색 필터
      if (filters.search && !visitation.memberName.toLowerCase().includes(filters.search.toLowerCase())) {
        return false;
      }
      
      // 상태 필터
      if (filters.status !== 'all' && visitation.status !== filters.status) {
        return false;
      }
      
      // 유형 필터
      if (filters.type !== 'all' && visitation.type !== filters.type) {
        return false;
      }
      
      // 시작일 필터
      if (filters.startDate) {
        const visitDate = new Date(visitation.visitDate.toDate());
        const filterDate = new Date(filters.startDate);
        if (visitDate < filterDate) {
          return false;
        }
      }
      
      // 종료일 필터
      if (filters.endDate) {
        const visitDate = new Date(visitation.visitDate.toDate());
        const filterDate = new Date(filters.endDate);
        filterDate.setHours(23, 59, 59, 999); // 하루 종료 시간으로 설정
        if (visitDate > filterDate) {
          return false;
        }
      }
      
      return true;
    });
  }, [visitations, filters]);

  // 통계 계산
  const statistics = useMemo(() => {
    const totalVisitations = visitations.length;
    const completedVisitations = visitations.filter(v => v.status === '완료').length;
    const scheduledVisitations = visitations.filter(v => v.status === '예정').length;
    const followUpNeeded = visitations.filter(v => v.status === '후속조치 필요').length;

    return { totalVisitations, completedVisitations, scheduledVisitations, followUpNeeded };
  }, [visitations]);

  // 로딩 상태 처리
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8">
        <div className="space-y-4">
          {[...Array(5)].map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gray-300 dark:bg-gray-600 rounded"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // 오류 상태 처리
  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20">
            <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">데이터 로딩 오류</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  // 상태별 스타일
  const getStatusStyle = (status: string) => {
    switch (status) {
      case '완료':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case '예정':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case '후속조치 필요':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case '취소':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  // 유형별 아이콘
  const getTypeIcon = (type: string) => {
    switch (type) {
      case '정기 심방':
        return <FaCalendarAlt className="h-4 w-4" />;
      case '특별 심방':
        return <FaUsers className="h-4 w-4" />;
      case '긴급 심방':
        return <FaExclamationTriangle className="h-4 w-4" />;
      case '신입 심방':
        return <FaHome className="h-4 w-4" />;
      default:
        return <FaClipboardList className="h-4 w-4" />;
    }
  };

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatVisitors = (visitors: string[] | string) => {
    if (Array.isArray(visitors)) {
      return visitors.join(', ');
    }
    return visitors;
  };

  if (visitations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center">
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gray-100 dark:bg-gray-700">
          <FaHome className="h-8 w-8 text-gray-400 dark:text-gray-500" />
        </div>
        <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">등록된 심방 기록이 없습니다</h3>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          새로운 심방 기록을 추가해주세요.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
      {/* 헤더 및 통계 */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">심방 기록 관리</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              총 {statistics.totalVisitations}개의 심방 기록이 있습니다.
            </p>
          </div>
          
          {/* 필터 버튼 */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-150"
          >
            <FaFilter className="mr-2 h-4 w-4" />
            필터
          </button>
        </div>
        
        {/* 통계 정보 */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
            <div className="flex items-center">
              <FaClipboardList className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" />
              <div>
                <p className="text-sm font-medium text-blue-800 dark:text-blue-300">총 심방 수</p>
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{statistics.totalVisitations}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
            <div className="flex items-center">
              <FaCheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
              <div>
                <p className="text-sm font-medium text-green-800 dark:text-green-300">완료</p>
                <p className="text-2xl font-bold text-green-900 dark:text-green-100">{statistics.completedVisitations}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg">
            <div className="flex items-center">
              <FaCalendarAlt className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-2" />
              <div>
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">예정</p>
                <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-100">{statistics.scheduledVisitations}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
            <div className="flex items-center">
              <FaExclamationTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" />
              <div>
                <p className="text-sm font-medium text-red-800 dark:text-red-300">후속조치</p>
                <p className="text-2xl font-bold text-red-900 dark:text-red-100">{statistics.followUpNeeded}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 검색 및 필터 영역 */}
      {showFilters && (
        <div className="p-6 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">상태</label>
              <select
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="all">전체</option>
                <option value="예정">예정</option>
                <option value="완료">완료</option>
                <option value="후속조치 필요">후속조치 필요</option>
                <option value="취소">취소</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">유형</label>
              <select
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.type}
                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
              >
                <option value="all">전체</option>
                <option value="정기 심방">정기 심방</option>
                <option value="특별 심방">특별 심방</option>
                <option value="긴급 심방">긴급 심방</option>
                <option value="신입 심방">신입 심방</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">시작일</label>
              <input
                type="date"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">종료일</label>
              <input
                type="date"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </div>
            
            <div className="flex items-end">
              <button
                onClick={() => setFilters({
                  search: '',
                  status: 'all',
                  type: 'all',
                  startDate: '',
                  endDate: ''
                })}
                className="w-full px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors duration-150"
              >
                초기화
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 검색창 */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="relative max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaSearch className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="대상자 이름으로 검색..."
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150"
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
          />
        </div>
      </div>
      
      {/* 필터 결과 표시 */}
      <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          총 <span className="font-semibold text-gray-900 dark:text-white">{visitations.length}</span>개의 심방 기록 중
          <span className="font-semibold text-blue-600 dark:text-blue-400"> {filteredVisitations.length}</span>개가 표시됨
        </p>
      </div>

      {/* Desktop Table */}
      <div className="overflow-x-auto hidden md:block">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">번호</th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">심방 날짜</th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">대상자</th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">방문자</th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">유형</th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">상태</th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">관리</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredVisitations.map((visitation, index) => (
              <tr key={visitation.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150" role="row">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-500 dark:text-gray-400 font-medium" role="cell">{index + 1}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-400" role="cell">{formatDate(visitation.visitDate.toDate())}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900 dark:text-gray-200 font-medium" role="cell">{visitation.memberName}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-400" role="cell">{formatVisitors(visitation.visitors)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center" role="cell">
                  <div className="flex items-center justify-center space-x-2">
                    {getTypeIcon(visitation.type)}
                    <span>{visitation.type}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center" role="cell">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusStyle(visitation.status)}`}>
                    {visitation.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center" role="cell">
                  <div className="flex items-center justify-center space-x-2">
                    <button
                      onClick={() => onEdit(visitation)}
                      className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded"
                      aria-label={`Edit ${visitation.memberName}'s visitation`}
                    >
                      <FaEdit size={14} />
                    </button>
                    <button
                      onClick={() => onDelete(visitation.id)}
                      className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
                      aria-label={`Delete ${visitation.memberName}'s visitation`}
                    >
                      <FaTrashAlt size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
        {filteredVisitations.map((visitation, index) => (
          <div key={visitation.id} className="p-4 bg-white dark:bg-gray-800 transition-colors duration-150">
            {/* 헤더 영역 */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white">
                  <FaHome />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white">{visitation.memberName}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">#{index + 1}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusStyle(visitation.status)}`}>
                  {visitation.status}
                </span>
                <button className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  <FaEllipsisV size={16} />
                </button>
              </div>
            </div>
            
            {/* 정보 영역 */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center space-x-2">
                <FaCalendarAlt className="h-4 w-4 text-gray-400" />
                <span className="text-gray-700 dark:text-gray-300">
                  <span className="font-medium">날짜:</span> {formatDate(visitation.visitDate.toDate())}
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                {getTypeIcon(visitation.type)}
                <span className="text-gray-700 dark:text-gray-300">
                  <span className="font-medium">유형:</span> {visitation.type}
                </span>
              </div>
              
              <div className="flex items-center space-x-2 col-span-2">
                <FaUsers className="h-4 w-4 text-gray-400" />
                <span className="text-gray-700 dark:text-gray-300">
                  <span className="font-medium">방문자:</span> {formatVisitors(visitation.visitors)}
                </span>
              </div>
            </div>
            
            {/* 관리 버튼 */}
            <div className="flex justify-end space-x-2 mt-3">
              <button
                onClick={() => onEdit(visitation)}
                className="px-3 py-1 text-xs text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded transition-colors duration-150"
              >
                수정
              </button>
              <button
                onClick={() => onDelete(visitation.id)}
                className="px-3 py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors duration-150"
              >
                삭제
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default VisitationList;
